import ReactHtmlParser from 'react-html-parser';
import { BLOCKS } from '@contentful/rich-text-types';
import React from 'react';
import { useIntl } from 'react-intl';
import Seo from '../../../Seo/Seo';
import Gallery from '../../../elements/Gallery/Gallery';
import RichText from '../../../UI/RichText';
import SpecsChart from '../SpecsChart/SpecsChart';
import Button from '../../../UI/Button/Button';
import BackToPageLink from '../../../UI/BackToPageLink/BackToPageLink';
import { Container, GalleryWrapper, SpecsWrapper } from '../styles';
import MobileShopCards from '../../../MobileShopCards/MobileShopCards';
import { isKiosk } from '../../../../utils/isKiosk';
import { DeviceInfoWrapper, PhoneTitle, TitleText, BodyText } from './styles';

interface Props {
  data: Record<string, any>;
  preferredProvince?: string;
}

const detailsHeadingOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <TitleText>{children}</TitleText>,
  },
};

const detailsBodyOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyText>{children}</BodyText>,
  },
};

const InactivePhoneScreen = ({ preferredProvince = '', data }: Props) => {
  // This page has two versions. If a phone is active, it will render the full details of the offers, variants etc via the PhoneDetailsScreen.
  // If a phone is inactive (has no active variants) it will render a different version via this file.
  const phone = data.contentfulPhone;
  const { locale } = useIntl();
  const isKioskMode = isKiosk();
  // META DATA
  // ========================
  const phoneMetaTitle = phone.displayName.concat(
    data.contentfulPage.metaTitle,
  );
  const phoneMetaDescription = data.contentfulPage.metaDescription.metaDescription
    .replace('%PHONE_MODEL%', phone.displayName)
    .replace('%CARRIERS%', 'national carriers');

  // SPEC LABELS
  // ========================
  const specLabelsSection = data.contentfulPage.pageSections.find((section) => section.contentful_id === '2kkZtaYPbAxkjZFGg4Qvl5');
  const specLabels = specLabelsSection.formFields.reduce((obj, item) => {
    obj[item.contentful_id] = item.displayLabel;
    return obj;
  }, {});

  // INACTIVE PHONE TEXT
  // ========================
  const inactiveSection = data.contentfulPage.pageSections.find((section) => section.contentful_id === '4Z1n7io6v688Ipjx9FH1YR').combinatorSectionItems;
  const inactiveText = inactiveSection.find((section) => section.contentful_id === '2YpRdkdhWXCDYnA019aMEz');
  const inactiveCTA = inactiveSection.find((section) => section.contentful_id === '6XFeZTOEwdGtq9WyGxszoT');

  // IMAGES
  // ========================
  const imageSet = {
    phoneFrontImage: phone.featuredPhoneFrontImage,
    phoneBackImage: phone.featuredPhoneBackImage,
  };

  // CALL TO ACTION CARDS
  // ========================
  // Call to action card section
  const cardsSection = data.contentfulPage.pageSections.find((section) => section.contentful_id === '3uhHNOZLVcAjG3WnNjJodv');

  // Get card section header
  const headingSection = cardsSection.combinatorSectionItems.find((item) => item.__typename === 'ContentfulTextSection');
  // Get cards from data
  const cards = cardsSection.combinatorSectionItems.filter((item) => item.__typename === 'ContentfulCardItem');
  // Get blades from data
  const bladeData = cardsSection.combinatorSectionItems.filter((item) => item.__typename === 'ContentfulBlade');

  return (
    <>
      <Seo title={phoneMetaTitle} description={phoneMetaDescription} />
      <Container>
        {/* BACK TO PHONES */}
        <BackToPageLink
          label={ReactHtmlParser(specLabels['1JOKfYsM6SmBXibY0ewFey'])}
          to={`/${locale}/phones`}
        />

        {/* IMAGES */}
        <GalleryWrapper>
          <Gallery imageSet={imageSet} isPhoneActive={false} />
        </GalleryWrapper>

        {/* PHONE TEXT */}
        <DeviceInfoWrapper>
          <PhoneTitle>{ReactHtmlParser(phone.displayName)}</PhoneTitle>
          <RichText
            document={inactiveText.heading}
            options={detailsHeadingOptions}
          />
          <RichText
            document={inactiveText.contentBody}
            options={detailsBodyOptions}
          />
          {!isKioskMode && (
            <div className="buttonWrapper">
              <Button
                buttonText={inactiveCTA.ctaLabel.ctaLabel}
                buttonColour={inactiveCTA.ctaColor}
                buttonSize="large"
                route={inactiveCTA.ctaTarget}
                locale={preferredProvince}
              />
            </div>
          )}
        </DeviceInfoWrapper>

        {/* SPECS CHART */}
        <SpecsWrapper>
          <SpecsChart phone={phone} locale={locale} specLabels={specLabels} />
        </SpecsWrapper>

        <MobileShopCards
          heading={headingSection.heading}
          cards={cards}
          bladeData={bladeData}
        />
      </Container>
    </>
  );
};

export default InactivePhoneScreen;
